import { FormEvent, useState } from 'react'

import * as api from 'api/api'
import { Subcontractor, SubcontractorInput, SubcontractorStatus } from 'api/types'
import { useAuth } from 'auth/AuthContext'
import { useConfirm } from 'components/Confirm'
import { Button, Card, EmptyState, ErrorState, IconButton, Sheet, Spinner, TextField } from 'components/ui'
import { SUBCONTRACTOR_STATUS_BADGE_CLASS, SUBCONTRACTOR_STATUS_LABELS, formatNumber } from 'lib/format'
import { useAction, useAsync } from 'lib/hooks'
import { t } from 'lib/i18n'
import { ForbiddenPage } from 'pages/system/ForbiddenPage'

const STATUS_OPTIONS: SubcontractorStatus[] = ['ACTIVE', 'ON_HOLD', 'ENDED']

const EMPTY_FORM: SubcontractorInput = {
  contactName: '',
  name: '',
  phone: '',
  status: 'ACTIVE',
  trades: [],
}

const trend = (current: number, previous: number) => {
  if (!previous) return null
  const pct = Math.round(((current - previous) / previous) * 100)
  return { label: `${pct > 0 ? '+' : ''}${pct}%`, tone: pct >= 0 ? ('good' as const) : ('bad' as const) }
}

const SubcontractorForm = ({
  initial,
  busy,
  onCancel,
  onSubmit,
}: {
  initial?: Subcontractor
  busy: boolean
  onCancel: () => void
  onSubmit: (input: SubcontractorInput) => void
}) => {
  const [form, setForm] = useState<SubcontractorInput>(() =>
    initial
      ? { contactName: initial.contactName ?? '', name: initial.name, phone: initial.phone ?? '', status: initial.status, trades: initial.trades }
      : EMPTY_FORM
  )
  const [tradesText, setTradesText] = useState(initial?.trades.join(t('، ', ', ')) ?? '')

  const valid = form.name.trim()

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!valid) return
    onSubmit({
      contactName: form.contactName?.trim() || null,
      name: form.name.trim(),
      phone: form.phone?.trim() || null,
      status: form.status,
      trades: tradesText
        .split(/[،,]/)
        .map((trade) => trade.trim())
        .filter(Boolean),
    })
  }

  return (
    <Sheet
      title={initial ? t('تعديل مقاول فرعي', 'Edit subcontractor') : t('إضافة مقاول فرعي', 'Add subcontractor')}
      onClose={onCancel}
      footer={
        <>
          <Button onClick={onCancel}>{t('إلغاء', 'Cancel')}</Button>
          <Button type="submit" form="subcontractor-form" variant="primary" busy={busy} disabled={!valid}>
            {initial ? t('حفظ', 'Save') : t('إضافة', 'Add')}
          </Button>
        </>
      }
    >
      <form id="subcontractor-form" className="form-grid" onSubmit={submit}>
        <TextField
          label={t('اسم الشركة', 'Company name')}
          className="span-2"
          value={form.name}
          onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
          placeholder={t('مثال: شركة المصري للمقاولات', 'Example: Al-Masri Contracting')}
          autoFocus
          required
        />
        <TextField
          label={t('الشخص المسؤول', 'Contact person')}
          value={form.contactName ?? ''}
          onChange={(event) => setForm((current) => ({ ...current, contactName: event.target.value }))}
        />
        <TextField
          label={t('رقم الهاتف', 'Phone number')}
          dir="ltr"
          value={form.phone ?? ''}
          onChange={(event) => setForm((current) => ({ ...current, phone: event.target.value }))}
        />
        <TextField
          label={t('التخصصات', 'Trades')}
          className="span-2"
          hint={t('افصل بين التخصصات بفاصلة، مثال: حدادة، نجارة مسلح', 'Separate trades with a comma, for example: steel fixing, formwork')}
          value={tradesText}
          onChange={(event) => setTradesText(event.target.value)}
          placeholder={t('مثال: حدادة، نجارة مسلح', 'Example: steel fixing, formwork')}
        />
        <div className="span-2">
          <label className="field__label" htmlFor="sub-status">
            {t('الحالة', 'Status')} <span className="field__required">*</span>
          </label>
          <select
            id="sub-status"
            className="input"
            value={form.status}
            onChange={(event) => setForm((current) => ({ ...current, status: event.target.value as SubcontractorStatus }))}
          >
            {STATUS_OPTIONS.map((status) => (
              <option key={status} value={status}>
                {SUBCONTRACTOR_STATUS_LABELS[status]}
              </option>
            ))}
          </select>
        </div>
      </form>
    </Sheet>
  )
}

/** Global crew view — admin console, portfolio-wide (Figma: Admin / 09 · Crew). Trades are entirely real, derived
 *  from StaffLine entries across every project's reports; Subcontractors is a small admin-managed roster, cross-
 *  referenced against the optional subcontractor tag engineers can now set on a staff line in the report form. */
export const CrewPage = () => {
  const { user } = useAuth()
  const { data: overview, error, loading, reload } = useAsync(api.fetchCrewOverview, [])
  const { busy, run } = useAction()
  const confirm = useConfirm()
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<Subcontractor | null>(null)

  if (!user?.isAdmin) return <ForbiddenPage message={t('صفحة الكوادر متاحة لمدير النظام فقط.', 'The crew page is available to admins only.')} />

  const workersTrend = overview ? trend(overview.workersToday, overview.workersYesterday) : null
  const avgTrend = overview ? trend(overview.avgWorkersPerDay30d, overview.avgWorkersPerDay30dPrev) : null

  const create = async (input: SubcontractorInput) => {
    const created = await run('add', () => api.createSubcontractor(input), t('تمت إضافة المقاول الفرعي', 'Subcontractor added'))
    if (created) {
      await reload()
      setAdding(false)
    }
  }

  const update = async (input: SubcontractorInput) => {
    if (!editing) return
    const updated = await run(`edit${editing.id}`, () => api.updateSubcontractor(editing.id, input), t('تم حفظ التعديلات', 'Changes saved'))
    if (updated) {
      await reload()
      setEditing(null)
    }
  }

  const remove = async (item: Subcontractor) => {
    if (!(await confirm(t(`حذف «${item.name}» من قائمة المقاولين الفرعيين؟`, `Delete "${item.name}" from the subcontractor list?`), { confirmLabel: t('حذف', 'Delete') }))) return
    const done = await run(`rm${item.id}`, () => api.removeSubcontractor(item.id), t('تم الحذف', 'Deleted'))
    if (done) await reload()
  }

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1>{t('الكوادر', 'Crew')}</h1>
          <p className="muted">{t('التخصصات والمقاولون الفرعيون عبر كل المواقع · اليوم', 'Trades and subcontractors across all sites · today')}</p>
        </div>
        <Button variant="primary" icon="plus" onClick={() => setAdding(true)}>
          {t('إضافة مقاول فرعي', 'Add subcontractor')}
        </Button>
      </div>

      {loading && !overview ? (
        <Spinner />
      ) : error ? (
        <ErrorState message={error} onRetry={reload} />
      ) : !overview ? null : (
        <>
          <div className="stats stats--static" role="group" aria-label={t('مؤشرات الكوادر', 'Crew indicators')}>
            <div className="stat">
              <span className="stat__value">{formatNumber(overview.workersToday)}</span>
              <span className="stat__label">{t('الكوادر في الموقع اليوم', 'Crew on site today')}</span>
              {workersTrend ? <span className={`small ${workersTrend.tone === 'good' ? 'plan-ahead' : 'plan-behind'}`}>{workersTrend.label} {t('عن أمس', 'vs yesterday')}</span> : null}
            </div>
            <div className="stat">
              <span className="stat__value">{overview.tradesCount}</span>
              <span className="stat__label">{t('التخصصات', 'Trades')}</span>
              <span className="small muted">{t(`عبر ${overview.activeSitesToday} موقعاً نشطاً`, `Across ${overview.activeSitesToday} active sites`)}</span>
            </div>
            <div className="stat">
              <span className="stat__value">{overview.subcontractors.length}</span>
              <span className="stat__label">{t('المقاولون الفرعيون', 'Subcontractors')}</span>
              <span className="small muted">
                {t(
                  `${overview.subcontractorsActive} نشط · ${overview.subcontractorsOnHold} متوقف · ${overview.subcontractorsEnded} منتهٍ`,
                  `${overview.subcontractorsActive} Active · ${overview.subcontractorsOnHold} On hold · ${overview.subcontractorsEnded} Ended`
                )}
              </span>
            </div>
            <div className="stat">
              <span className="stat__value">{formatNumber(overview.avgWorkersPerDay30d)}</span>
              <span className="stat__label">{t('متوسط الكوادر / يوم (30 يوم)', 'Average crew / day (30 days)')}</span>
              {avgTrend ? <span className={`small ${avgTrend.tone === 'good' ? 'plan-ahead' : 'plan-behind'}`}>{avgTrend.label} {t('آخر 30 يوم', 'last 30 days')}</span> : null}
            </div>
          </div>

          <Card title={t(`التخصصات (${overview.trades.length})`, `Trades (${overview.trades.length})`)}>
            <p className="muted small">{t('الكوادر في الموقع اليوم حسب التخصص، من التقارير المُرسلة', 'Crew on site today by trade, from submitted reports')}</p>
            {!overview.trades.length ? (
              <EmptyState title={t('لا توجد بيانات كوادر اليوم', 'No crew data for today')} icon="users" />
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th scope="col">{t('التخصص', 'Trade')}</th>
                      <th scope="col">{t('المقاولون الفرعيون', 'Subcontractors')}</th>
                      <th scope="col">{t('المواقع', 'Sites')}</th>
                      <th scope="col">{t('الكوادر اليوم', 'Crew today')}</th>
                      <th scope="col">{t('متوسط/يوم (30 يوم)', 'Average/day (30 days)')}</th>
                      <th scope="col">{t('نسبة من الكوادر', 'Share of crew')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overview.trades.map((trade) => (
                      <tr key={trade.title}>
                        <td>
                          <strong>{trade.title}</strong>
                        </td>
                        <td>{trade.subcontractorCount || <span className="muted">—</span>}</td>
                        <td>{trade.sitesCount}</td>
                        <td>{trade.workersToday}</td>
                        <td>{formatNumber(trade.avgPerDay30d)}</td>
                        <td>{trade.shareOfCrewPct}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card title={t(`المقاولون الفرعيون (${overview.subcontractors.length})`, `Subcontractors (${overview.subcontractors.length})`)}>
            <p className="muted small">{t('الشركات الموردة للكوادر · الكوادر اليوم من التقارير المُرسلة', "Companies supplying crew · today's crew from submitted reports")}</p>
            {!overview.subcontractors.length ? (
              <EmptyState title={t('لا يوجد مقاولون فرعيون مسجّلون بعد', 'No subcontractors recorded yet')} icon="users">
                <Button variant="primary" icon="plus" onClick={() => setAdding(true)}>
                  {t('إضافة مقاول فرعي', 'Add subcontractor')}
                </Button>
              </EmptyState>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th scope="col">{t('المقاول الفرعي', 'Subcontractor')}</th>
                      <th scope="col">{t('التخصصات', 'Trades')}</th>
                      <th scope="col">{t('المواقع اليوم', 'Sites today')}</th>
                      <th scope="col">{t('الكوادر اليوم', 'Crew today')}</th>
                      <th scope="col">{t('الحالة', 'Status')}</th>
                      <th scope="col">
                        <span className="sr-only">{t('إجراءات', 'Actions')}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {overview.subcontractors.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <strong>{item.name}</strong>
                          {item.contactName || item.phone ? (
                            <div className="muted small" dir={item.phone ? 'ltr' : undefined}>
                              {[item.contactName, item.phone].filter(Boolean).join(' · ')}
                            </div>
                          ) : null}
                        </td>
                        <td>{item.trades.length ? item.trades.join(t('، ', ', ')) : <span className="muted">—</span>}</td>
                        <td>{item.sitesToday}</td>
                        <td>{item.workersToday}</td>
                        <td>
                          <span className={SUBCONTRACTOR_STATUS_BADGE_CLASS[item.status]}>{SUBCONTRACTOR_STATUS_LABELS[item.status]}</span>
                        </td>
                        <td>
                          <div className="row-actions">
                            <IconButton icon="edit" label={t(`تعديل ${item.name}`, `Edit ${item.name}`)} onClick={() => setEditing(item)} />
                            <IconButton
                              icon="trash"
                              label={t(`حذف ${item.name}`, `Delete ${item.name}`)}
                              onClick={() => remove(item)}
                              disabled={busy === `rm${item.id}`}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}

      {adding ? <SubcontractorForm busy={busy === 'add'} onCancel={() => setAdding(false)} onSubmit={create} /> : null}
      {editing ? (
        <SubcontractorForm initial={editing} busy={busy === `edit${editing.id}`} onCancel={() => setEditing(null)} onSubmit={update} />
      ) : null}
    </div>
  )
}
